import "./Admins.scss";
import { useState, useEffect } from "react";
import GreenButton from "../../components/GreenButton/GreenButton";
import BlueButton from "../../components/BlueButton/BlueButton";
import EditUser from "./../../assets/edit-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import AddGif from "./../../assets/add-gif.gif";
import { TextField, useMediaQuery } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import { AdminsColumns, AdminsPermissionsColumns } from "../../SourceData";
import MessageModals from "../../components/MessageModals/MessageModals";
import api from "../../utils/api"; // Import your API instance
import { toast } from "react-toastify";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import { DatePicker } from "antd";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import { useNavigate } from "react-router-dom";
import usePermissions from "../../hooks/usePermissions";
const { RangePicker } = DatePicker;

const Admins = () => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [query, setQuery] = useState("");
  const [view, setView] = useState("admins");
  const [currentModalView, setCurrentModalView] = useState("changePassword");
  const [openDelete, setOpenDelete] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const { hasPermission } = usePermissions();
  // Add state for date range
  const [dateRange, setDateRange] = useState(null);

  const navigate = useNavigate();
  // State for API data
  const [adminsData, setAdminsData] = useState([]);
  const [permissionsData, setPermissionsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchData, setFetchData] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  // Fetch data from API based on current view
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.length >= 2 || query.length === 0) {
        // Reset to first page when search/filter changes
        setPage(0);
        setFetchData(!fetchData);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [query, dateRange]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: page + 1, // Convert 0-based to 1-based page numbering
          limit: pageSize, // Changed from per_page to limit based on your API response
        });

        if (query.length >= 2) {
          params.append("search", query);
        }

        if (view === "admins") {
          const response = await api.get(`/supervisors?${params.toString()}`);
          if (response.data && response.data.data) {
            setAdminsData(response?.data?.data);
            setTotalRows(response.data.total || 0);
          }
        } else if (view === "permissions") {
          const response = await api.get("/roles");
          if (response.data && response.data.data) {
            // Transform the data to match the DataGrid expected format
            const formattedData = response.data.data.map((role, index) => ({
              id: role.id,
              index: index + 1,
              name: role.name,
              active: role.active,
              users_count: role.users_count,
            }));
            setPermissionsData(formattedData);
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.response?.data?.message || "حدث خطأ أثناء جلب البيانات");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [view, fetchData, page, pageSize]);

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const handleOpen = (view, item = null) => {
    setCurrentModalView(view);
    if (item) {
      setSelectedItem(item);
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedItem(null);
    setFetchData(!fetchData);
  };

  const handleActive = async (id) => {
    setLoading(true);
    try {
      if (view === "admins") {
        await api.put(`/supervisors/${id}/activateToggle`);
        setFetchData(!fetchData);
        toast.success("تم تعديل الحالة بنجاح");
      } else {
        await api.put(`/role/${id}/activateToggle`);
        setFetchData(!fetchData);
        toast.success("تم تعديل الحالة بنجاح");
      }
    } catch (error) {
      setError(error.response?.data?.message || "حدث خطأ أثناء تعديل الحالة");
    } finally {
      setLoading(false);
    }
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const adminsActionColumn = [
    hasPermission(15) && {
      field: "active",
      headerName: "الحالة",
      minWidth: 80,
      flex: 1,
      headerAlign: "right",
      align: "left",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.active === true;
        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row.id)}
          />
        );
      },
    },

    {
      field: "action",
      headerName: "",
      width: 100,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            {hasPermission(17) && (
              <div
                className="cell-btn"
                onClick={() => handleOpen("editAdmin", params.row)}
              >
                <img src={EditUser} alt="" />
              </div>
            )}
            {hasPermission(18) && (
              <div
                className="cell-btn"
                onClick={() => {
                  setSelectedItem(params.row);
                  setOpenDelete(true);
                }}
              >
                <img src={DeleteUser} alt="" />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const permissionsActionColumn = [
    hasPermission(20) && {
      field: "active",
      headerName: "الحالة",
      minWidth: 80,
      flex: 1,
      headerAlign: "right",
      align: "left",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.active === true;
        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row.id)}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 160,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            {hasPermission(21) && (
              <div
                className="cell-btn"
                onClick={() => navigate(`/admins/permissions/${params.row.id}`)}
              >
                <img src={EditUser} alt="" />
              </div>
            )}
            {hasPermission(184) && (
              <div
                className="cell-btn"
                onClick={() => {
                  setSelectedItem(params.row);
                  setOpenDelete(true);
                }}
              >
                <img src={DeleteUser} alt="" />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  // Filter data based on search query and date range
  const filteredAdminsData = adminsData.filter((admin) => {
    // Text search filter
    const matchesSearch =
      admin.name?.toLowerCase().includes(query.toLowerCase()) ||
      admin.email?.toLowerCase().includes(query.toLowerCase()) ||
      admin.phone?.includes(query);

    // Date filter
    let matchesDateRange = true;
    if (dateRange && dateRange[0] && dateRange[1] && admin.created_at) {
      // Parse the admin created_at date which is in format "YYYY-MM-DD HH:MM:SS am/pm"
      const parts = admin.created_at.split(" ");
      const datePart = parts[0]; // YYYY-MM-DD
      const timePart = parts[1]; // HH:MM:SS
      const amPmPart = parts[2]; // am/pm

      // Create a Date object from the parsed parts
      let [year, month, day] = datePart
        .split("-")
        .map((num) => parseInt(num, 10));
      const [hours, minutes, seconds] = timePart
        .split(":")
        .map((num) => parseInt(num, 10));

      // JavaScript months are 0-indexed
      month = month - 1;

      // Adjust hours for PM
      let adjustedHours = hours;
      if (amPmPart.toLowerCase() === "pm" && hours < 12) {
        adjustedHours = hours + 12;
      } else if (amPmPart.toLowerCase() === "am" && hours === 12) {
        adjustedHours = 0;
      }

      const adminDate = new Date(
        year,
        month,
        day,
        adjustedHours,
        minutes,
        seconds
      );

      // Get start and end dates from the date picker
      const startDate = new Date(dateRange[0].format("YYYY-MM-DD"));
      const endDate = new Date(dateRange[1].format("YYYY-MM-DD"));
      endDate.setHours(23, 59, 59, 999); // Set to end of day

      matchesDateRange = adminDate >= startDate && adminDate <= endDate;
    }

    return matchesSearch && matchesDateRange;
  });

  const filteredPermissionsData = permissionsData.filter((role) =>
    role.name?.toLowerCase().includes(query.toLowerCase())
  );

  // Reset date range when view changes
  useEffect(() => {
    setDateRange(null);
  }, [view]);

  return (
    <div className="admins">
      <div className="admins-header">
        <p className="title">المسؤولين الفرعيين</p>

        <div className="btns-container">
          <GreenButton
            text="المسؤولين الفرعيين"
            size="medium"
            variant={view === "admins" ? "fill" : "outlined"}
            onClick={() => setView("admins")}
          />
          {hasPermission(19) && (
            <GreenButton
              text="الصلاحيات"
              size="small"
              variant={view === "permissions" ? "fill" : "outlined"}
              onClick={() => setView("permissions")}
            />
          )}
        </div>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder={
                view === "admins"
                  ? "ابحث بأسم المسؤول / البريد الالكتروني او رقم الموبايل"
                  : "بحث..."
              }
              onChange={(e) => setQuery(e.target.value)}
              sx={{
                width: isMobile
                  ? "100%"
                  : isTablet || view === "permissions"
                  ? "300px"
                  : "616px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            {view === "admins" && (
              <RangePicker
                format={"MMM D"}
                style={{
                  width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                  padding: "0px 10px",
                  direction: "ltr",
                }}
                placeholder={["من", "الي"]}
                className="range-picker"
                onChange={handleDateRangeChange}
                value={dateRange}
              />
            )}
          </div>

          {view === "admins"
            ? hasPermission(16) && (
                <BlueButton
                  text="اضافة مسؤول"
                  size={isMobile ? "full" : isTablet ? "fit" : "medium"}
                  variant="fill"
                  onClick={() => handleOpen("createAdmin")}
                  icon={<img src={AddGif} alt="" />}
                />
              )
            : hasPermission(183) && (
                <BlueButton
                  text="اضافة صلاحية جديدة"
                  size={isMobile ? "full" : isTablet ? "fit" : "large"}
                  variant="fill"
                  path="/admins/permissions"
                  icon={<img src={AddGif} alt="" />}
                />
              )}
        </div>
      </div>
      <div className="container">
        {error && <div className="error-message">{error}</div>}

        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          loading={loading}
          columns={
            view === "admins"
              ? AdminsColumns.concat(adminsActionColumn.filter(Boolean))
              : AdminsPermissionsColumns.concat(
                  permissionsActionColumn.filter(Boolean)
                )
          }
          rows={
            view === "admins" ? filteredAdminsData : filteredPermissionsData
          }
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          rowHeight={60}
          rowCount={totalRows}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={{
            page,
            pageSize,
          }}
          onPaginationModelChange={handlePaginationModelChange}
        />
      </div>

      <MessageModals
        open={openDelete}
        currentView="delete"
        route={
          view === "admins"
            ? `/supervisors/${selectedItem?.id}`
            : `/roles/${selectedItem?.id}`
        }
        onClose={() => {
          setOpenDelete(false);
        }}
        onDataChange={() => setFetchData(!fetchData)}
        text={
          view === "admins" ? `تم حذف المسؤل بنجاح` : `تم حذف الصلاحية بنجاح`
        }
      />

      <SwipeableModals
        open={isOpen}
        onClose={handleClose}
        onOpen={handleOpen}
        currentView={currentModalView}
        selectedItem={selectedItem}
      />
    </div>
  );
};

export default Admins;
